import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import {
  Box, AppBar, Toolbar, IconButton, Typography, TextField, Chip, Checkbox,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
  ListItemText, ListItemIcon, InputAdornment, Paper, Divider, CircularProgress,
  Alert, Snackbar, Card, CardContent, Avatar, Collapse, TextField as MuiTextField,
} from "@mui/material";
import ArrowBackIosNewIcon    from "@mui/icons-material/ArrowBackIosNew";
import SearchIcon             from "@mui/icons-material/Search";
import AddIcon                from "@mui/icons-material/Add";
import CloseIcon              from "@mui/icons-material/Close";
import LocationOnIcon         from "@mui/icons-material/LocationOn";
import ApartmentIcon          from "@mui/icons-material/Apartment";
import HomeWorkIcon           from "@mui/icons-material/HomeWork";
import MyLocationIcon         from "@mui/icons-material/MyLocation";
import WarningIcon            from "@mui/icons-material/Warning";
import NearMeIcon             from "@mui/icons-material/NearMe";
import ChatBubbleOutlineIcon  from "@mui/icons-material/ChatBubbleOutline";
import ExpandMoreIcon         from "@mui/icons-material/ExpandMore";
import ExpandLessIcon         from "@mui/icons-material/ExpandLess";
import { useNavigate }        from "react-router-dom";
import FormsBottomNavbar      from "../Post Module/FormsBottomNavbar";
import GuestLoginModal        from "../../GuestLoginModal";
import { AuthContext }        from "../../AuthContext/AuthContext";
import { BASE_URL }           from "./../../Api/ApiUrls";

// ─── Constants ────────────────────────────────────────────────────────────────
const RESPONSE_API_ENDPOINT = `${BASE_URL}/response-property-request/`;
const PAGE_SIZE = 20;

const MAIN_CATEGORIES = [
  { id: "Purchase",       label: "Purchase"       },
  { id: "Rent",           label: "Rent"           },
  { id: "Lease",          label: "Lease"          },
  { id: "JV/JD",          label: "JV/JD"          },
  { id: "Build to Suit",  label: "Build to Suit"  },
];

const PROPERTY_TYPE_OPTIONS = {
  Purchase:        ["Plot","Land","Rental with Duplex Building","Duplex House","PG","Office","Flat","Villas","Commercial Building","Apartment","Rental Building"],
  Rent:            ["House","Duplex House","Villa","Commercial Plot","Commercial Land","Commercial Building","PG","Office","Flat"],
  Lease:           ["House","Duplex House","Villa","Plot","Commercial Plot","Commercial Land","Commercial Building","PG","Office","Flat"],
  "JV/JD":         ["Land","Plot"],
  "Build to Suit": ["Land","Plot"],
};

// ── Extract just the city name from a location object ─────────────────────────
// This is what gets sent to the backend for LIKE matching.
// We send ONLY the primary city name, not the full address.
// e.g. location.name = "Hyderabad"        → sends "Hyderabad"
//      location.name = "📍 Secunderabad"  → sends "Secunderabad"
//      location.fullLocation = "Hyderabad, Telangana, India" → sends "Hyderabad"
const getLocationSearchKeyword = (loc) => {
  // 1. Start with the `name` field — it's already the short city/locality name
  const name = (loc.name || "")
    .replace("📍 ", "")
    .replace(/\s*\(\d+\)$/, "")
    .trim();

  if (name) return name;

  // 2. Fallback: take only first segment of fullLocation before comma
  const full = (loc.fullLocation || loc.fullAddress || "").trim();
  return full.split(",")[0].trim();
};

// ─── Skeleton card for loading state ─────────────────────────────────────────
const SkeletonCard = () => (
  <Box sx={{
    borderRadius: 2, overflow: "hidden", mb: 2,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    animation: "pulse 1.5s ease-in-out infinite",
    "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.4 } },
  }}>
    <Box sx={{ height: 44, backgroundColor: "#1a1a1a" }} />
    <Box sx={{ p: 1.5, border: "2px solid #8B7355", borderTop: "none", borderBottomLeftRadius: 8, borderBottomRightRadius: 8 }}>
      <Box sx={{ height: 12, backgroundColor: "#e0e0e0", borderRadius: 1, mb: 1, width: "70%" }} />
      <Box sx={{ height: 12, backgroundColor: "#e0e0e0", borderRadius: 1, mb: 1, width: "90%" }} />
      <Box sx={{ height: 12, backgroundColor: "#e0e0e0", borderRadius: 1, width: "50%" }} />
    </Box>
  </Box>
);

// ─── ScrollSentinel ───────────────────────────────────────────────────────────
const ScrollSentinel = ({ onVisible, hasMore, loading }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasMore && !loading) onVisible(); },
      { threshold: 0.1, rootMargin: "100px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onVisible, hasMore, loading]);

  return (
    <Box ref={ref} sx={{ py: 3, display: "flex", justifyContent: "center", alignItems: "center" }}>
      {loading && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <CircularProgress size={24} sx={{ color: "#8B7355" }} />
          <Typography variant="caption" sx={{ color: "#8B7355" }}>Loading more...</Typography>
        </Box>
      )}
      {!loading && !hasMore && (
        <Typography variant="caption" sx={{ color: "#999", fontStyle: "italic" }}>✓ All properties loaded</Typography>
      )}
    </Box>
  );
};

// ─── useInfiniteProperties ────────────────────────────────────────────────────
// ALL filtering done by backend. No client-side filtering.
const useInfiniteProperties = ({ selectedMainCategory, selectedPropertyTypes, selectedLocations }) => {
  const [items, setItems]                   = useState([]);
  const [cursor, setCursor]                 = useState(null);
  const [hasMore, setHasMore]               = useState(true);
  const [loading, setLoading]               = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // true on mount
  const [error, setError]                   = useState(null);
  const abortRef                            = useRef(null);

  // Build URL — send only clean city keywords to backend
  const buildUrl = useCallback((cursorValue) => {
    const params = new URLSearchParams({ limit: PAGE_SIZE });

    if (cursorValue)           params.set("cursor",         cursorValue);
    if (selectedMainCategory)  params.set("looking_for",    selectedMainCategory);
    if (selectedPropertyTypes?.length > 0)
      params.set("property_types", selectedPropertyTypes.join(","));

    // KEY FIX: send only the city name (e.g. "Hyderabad"), not the full address.
    // Use | as separator between multiple locations so comma inside a name doesn't break parsing.
    if (selectedLocations?.length > 0) {
      const keywords = selectedLocations
        .map(getLocationSearchKeyword)
        .filter(Boolean);
      if (keywords.length > 0)
        params.set("locations", keywords.join("|"));
    }

    return `https://landnest.net:7000/property-request/?${params.toString()}`;
  }, [selectedMainCategory, selectedPropertyTypes, selectedLocations]);

  const fetchPage = useCallback(async (cursorValue, isReset = false) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Show skeleton on reset (filter change / page load), spinner on loadMore
    if (isReset) setInitialLoading(true);
    else         setLoading(true);
    setError(null);

    try {
      const url  = buildUrl(cursorValue);
      const res  = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const results = data.results || [];
      setItems(prev => isReset ? results : [...prev, ...results]);
      setCursor(data.next_cursor ?? null);
      setHasMore(data.has_more ?? false);
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Fetch error:", err);
        setError(err.message);
      }
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [buildUrl]);

  // Stable filter key to avoid re-fetching on object identity changes
  const locationKey = selectedLocations.map(getLocationSearchKeyword).join("|");

  useEffect(() => {
    setItems([]);
    setCursor(null);
    setHasMore(true);
    setInitialLoading(true); // immediately show skeleton — no flash of "no properties"
    fetchPage(null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMainCategory, JSON.stringify(selectedPropertyTypes), locationKey]);

  const loadMore = useCallback(() => {
    if (!loading && !initialLoading && hasMore && cursor)
      fetchPage(cursor, false);
  }, [loading, initialLoading, hasMore, cursor, fetchPage]);

  return { items, hasMore, loading, initialLoading, error, loadMore };
};

// ─── Main Component ───────────────────────────────────────────────────────────
const PropertySearchFilters = () => {
  const navigate    = useNavigate();
  const { isGuest } = useContext(AuthContext);

  const [searchQuery,            setSearchQuery]            = useState("");
  const [selectedLocations,      setSelectedLocations]      = useState([]);
  const [useCurrentLocation,     setUseCurrentLocation]     = useState(false);
  const [propertyTypeDialogOpen, setPropertyTypeDialogOpen] = useState(false);
  const [selectedPropertyTypes,  setSelectedPropertyTypes]  = useState([]);
  const [searchResults,          setSearchResults]          = useState([]);
  const [showSearchResults,      setShowSearchResults]      = useState(false);
  const [isSearching,            setIsSearching]            = useState(false);
  const [error,                  setError]                  = useState(null);
  const [googleMapsResults,      setGoogleMapsResults]      = useState([]);
  const [showNoResults,          setShowNoResults]          = useState(false);
  const [currentLocation,        setCurrentLocation]        = useState(null);
  const [nearbyLocations,        setNearbyLocations]        = useState([]);
  const [selectedMainCategory,   setSelectedMainCategory]   = useState("");
  const [availablePropertyTypes, setAvailablePropertyTypes] = useState([]);
  const [activeCommentIndex,     setActiveCommentIndex]     = useState(null);
  const [commentText,            setCommentText]            = useState("");
  const [propertyLink,           setPropertyLink]           = useState("");
  const [responses,              setResponses]              = useState({});
  const [submitting,             setSubmitting]             = useState(false);
  const [expandedResponses,      setExpandedResponses]      = useState({});
  const [usersMap,               setUsersMap]               = useState({});
  const [showGuestModal,         setShowGuestModal]         = useState(false);
  const [apiData,                setApiData]                = useState([]);
  const searchTimeoutRef = useRef(null);
  const [searchCache,            setSearchCache]            = useState({});

  const { items: recentProperties, hasMore, loading: listLoading, initialLoading, loadMore } =
    useInfiniteProperties({ selectedMainCategory, selectedPropertyTypes, selectedLocations });

  const colors = {
    primary: "#1a1a1a", secondary: "#8B7355", accent: "#A52A2A",
    background: "#f5f5f5", cardBackground: "#FFFFFF",
    textDark: "#1a1a1a", textLight: "#FFFFFF", textMuted: "#666666",
    warning: "#ff9800", error: "#f44336",
    nearby: "#388e3c", exact: "#1976d2", google: "#f57c00",
  };

  useEffect(() => {
    fetchResponses();
    fetchLocationData();
    fetchUsersMap();
  }, []);

  const fetchUsersMap = () => {
    fetch(`${BASE_URL}/users/`)
      .then(r => r.json())
      .then(data => {
        const map = {};
        data.forEach(u => { map[u.user_id] = u.username; });
        setUsersMap(map);
      })
      .catch(e => console.error("Failed to fetch users", e));
  };

  const fetchLocationData = async () => {
    try {
      const res  = await fetch(`https://landnest.net:7000/property-request/?limit=200`);
      if (!res.ok) return;
      const data = await res.json();
      const processed = processApiData(data.results || []);
      setApiData(processed);
      localStorage.setItem("propertyLocationsCache", JSON.stringify(processed));
    } catch {
      const cached = localStorage.getItem("propertyLocationsCache");
      if (cached) { try { setApiData(JSON.parse(cached)); } catch (_) {} }
    }
  };

  const fetchResponses = async () => {
    try {
      const res = await fetch(RESPONSE_API_ENDPOINT);
      if (res.ok) {
        const data = await res.json();
        const byReqId = {};
        data.forEach(r => {
          if (!byReqId[r.req_id]) byReqId[r.req_id] = [];
          byReqId[r.req_id].push(r);
        });
        setResponses(byReqId);
      }
    } catch (e) { console.error("Error fetching responses:", e); }
  };

  useEffect(() => {
    if (selectedMainCategory && PROPERTY_TYPE_OPTIONS[selectedMainCategory]) {
      setAvailablePropertyTypes(PROPERTY_TYPE_OPTIONS[selectedMainCategory]);
    } else {
      setAvailablePropertyTypes([]);
      setSelectedPropertyTypes([]);
    }
  }, [selectedMainCategory]);

  const requireLogin = (e, cb) => {
    e.stopPropagation();
    if (isGuest) { setShowGuestModal(true); return; }
    cb();
  };

  const formatBudget = (min, max) => {
    if (!min && !max) return "Not specified";
    const fmt = n => {
      n = parseFloat(n) || 0;
      if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)} Cr`;
      if (n >= 100000)   return `₹${(n / 100000).toFixed(1)} L`;
      if (n >= 1000)     return `₹${(n / 1000).toFixed(1)} K`;
      return `₹${n}`;
    };
    const mF = fmt(min), xF = fmt(max);
    const sfx = s => s.includes("Cr") ? "Cr" : s.includes("L") ? "L" : s.includes("K") ? "K" : "";
    const ms = sfx(mF), xs = sfx(xF);
    if (parseFloat(min) === parseFloat(max)) return fmt(min);
    if (ms && ms === xs)
      return `₹${mF.replace(` ${ms}`, "").replace("₹", "")}-${xF.replace(` ${xs}`, "").replace("₹", "")} ${ms}`;
    return `${mF} - ${xF}`;
  };

  const formatDate = ds =>
    new Date(ds).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

  const formatCategoryName = c => !c ? "Property" : c.charAt(0).toUpperCase() + c.slice(1);

 const getPropertyLocation = (p) => {
  if (!p?.pro_loc?.length) return "Not specified";

  // selected searched locations
  const searchedKeywords = selectedLocations
    .map(loc => getLocationSearchKeyword(loc).toLowerCase());

  // find matched location
  const matchedLocation = p.pro_loc.find(loc => {
    const locationText = (loc.location || "").toLowerCase();

    return searchedKeywords.some(keyword =>
      locationText.includes(keyword)
    );
  });

  // return matched location if found
  if (matchedLocation) {
    return matchedLocation.location;
  }

  // fallback
  return p.pro_loc[0]?.location || "Not specified";
};

  const formatDimensions = p => {
    if (p.length > 0 && p.width > 0) {
      const area = p.length * p.width;
      return `${p.length}*${p.width} (${area}) ${p.units === "m" ? "sq m" : "sq ft"}`;
    }
    return null;
  };

  const calculateDistance = (la1, lo1, la2, lo2) => {
    const R = 6371, dL = (la2 - la1) * Math.PI / 180, dO = (lo2 - lo1) * Math.PI / 180;
    const a = Math.sin(dL / 2) ** 2 + Math.cos(la1 * Math.PI / 180) * Math.cos(la2 * Math.PI / 180) * Math.sin(dO / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const findNearbyLocations = (cLat, cLng, locs) => {
    if (!cLat || !cLng || !locs?.length) return [];
    return locs.filter(l => {
      const lat = parseFloat(l.lat), lng = parseFloat(l.long);
      return !isNaN(lat) && !isNaN(lng) && calculateDistance(cLat, cLng, lat, lng) <= 5;
    });
  };

  const processApiData = data => {
    if (!Array.isArray(data)) return [];
    const map = new Map();
    data.forEach(req => {
      (req?.pro_loc || []).forEach(loc => {
        const locName = loc?.location || "";
        if (!locName.trim()) return;
        const parts   = locName.split(",");
        const primary = parts[0]?.trim() || locName.trim();
        const city    = parts[1]?.trim() || primary;
        const key     = locName.trim();
        if (map.has(key)) {
          map.set(key, { ...map.get(key), count: map.get(key).count + 1 });
        } else {
          map.set(key, {
            name: primary, fullLocation: locName, city,
            type: determineLocationType(locName), count: 1,
            lat:  loc.lat  ? parseFloat(loc.lat)  : null,
            long: loc.long ? parseFloat(loc.long) : null,
            isFromAPI: true,
          });
        }
      });
    });
    return Array.from(map.values());
  };

  const determineLocationType = l => {
    l = (l || "").toLowerCase();
    if (l.includes("layout") || l.includes("colony") || l.includes("nagar") || l.includes("society") || l.includes("village") || l.includes("town")) return "locality";
    if (l.includes("apartment") || l.includes("towers") || l.includes("heights") || l.includes("complex") || l.includes("residency") || l.includes("estate")) return "project";
    if (l.includes("street") || l.includes("road") || l.includes("lane") || l.includes("avenue")) return "address";
    return "city";
  };

  const searchGoogleMaps = useCallback(async (query) => {
    if (!query.trim()) { setGoogleMapsResults([]); return []; }
    if (searchCache[query]) return searchCache[query];
    try {
      const res  = await fetch(`https://landnest.net:7000/property-request/maps/autocomplete?input=${encodeURIComponent(query)}`);
      const data = await res.json();
      if (data.status === "OK" && data.predictions) {
        const results = await Promise.all(
          data.predictions.slice(0, 3).map(async p => {
            try {
              const dRes  = await fetch(`https://landnest.net:7000/property-request/maps/place-details?place_id=${p.place_id}`);
              const dData = await dRes.json();
              return {
                id: p.place_id,
                // KEY FIX: store the short city name in `name`, not the full address
                name: p.structured_formatting.main_text,
                fullLocation: p.description,
                fullAddress:  p.description,
                description:  p.structured_formatting.secondary_text || "",
                type: "google", isFromGoogle: true,
                lat: dData.result?.geometry?.location?.lat,
                lng: dData.result?.geometry?.location?.lng,
              };
            } catch {
              return {
                id: p.place_id,
                name: p.structured_formatting.main_text,
                fullLocation: p.description,
                fullAddress:  p.description,
                description:  p.structured_formatting.secondary_text || "",
                type: "google", isFromGoogle: true,
              };
            }
          })
        );
        setSearchCache(prev => ({ ...prev, [query]: results }));
        setGoogleMapsResults(results);
        return results;
      }
      setGoogleMapsResults([]); return [];
    } catch {
      setGoogleMapsResults([]); return [];
    }
  }, [searchCache]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) { setError("Geolocation not supported."); setUseCurrentLocation(false); return; }
    setIsSearching(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      async ({ coords: { latitude, longitude } }) => {
        setCurrentLocation({ lat: latitude, lng: longitude });
        try {
          const res  = await fetch(`https://landnest.net:7000/property-request/maps/reverse-geocode?lat=${latitude}&lng=${longitude}`);
          const data = await res.json();
          let locationData;
          if (data.status === "OK" && data.results[0]) {
            const name  = data.results[0].formatted_address;
            const comps = data.results[0].address_components || [];
            const locality = comps.find(c => c.types.includes("locality"));
            const admin2   = comps.find(c => c.types.includes("administrative_area_level_2"));
            // Store short name — this is what gets sent to backend
            const short    = locality?.long_name || admin2?.long_name || name.split(",")[0] || "My Location";
            locationData = {
              id: `current_${Date.now()}`,
              name: `📍 ${short}`,        // "📍 Hyderabad" — cleanly extractable
              fullLocation: name,
              fullAddress:  name,
              type: "current", lat: latitude, long: longitude,
              isCurrentLocation: true, isFromGoogle: true, count: 0,
            };
          } else {
            locationData = {
              id: `current_${Date.now()}`, name: "📍 My Location",
              fullLocation: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
              fullAddress:  `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
              type: "current", lat: latitude, long: longitude,
              isCurrentLocation: true, count: 0,
            };
          }
          const filtered = selectedLocations.filter(l => !l.isCurrentLocation);
          setSelectedLocations(filtered.length >= 3 ? [...filtered.slice(1), locationData] : [...filtered, locationData]);
          const nearby = findNearbyLocations(latitude, longitude, apiData)
            .map(l => ({
              ...l, isNearby: true,
              distance: calculateDistance(latitude, longitude, parseFloat(l.lat), parseFloat(l.long)).toFixed(2),
              category: "nearby", section: "NEARBY WITHIN 5KM",
            }))
            .sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));
          setNearbyLocations(nearby);
          setSearchQuery(locationData.name.replace("📍 ", ""));
        } catch {
          setError("Error getting location. Please try manual search.");
        } finally {
          setIsSearching(false);
        }
      },
      err => {
        const msgs = {
          [err.PERMISSION_DENIED]:    "Location permission denied.",
          [err.POSITION_UNAVAILABLE]: "Location information unavailable.",
          [err.TIMEOUT]:              "Location request timed out.",
        };
        setError(msgs[err.code] || "Failed to get location.");
        setUseCurrentLocation(false);
        setIsSearching(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]); setGoogleMapsResults([]); setNearbyLocations([]);
      setShowSearchResults(false); setShowNoResults(false);
      return;
    }
    setIsSearching(true);
    setShowNoResults(false);
    setNearbyLocations([]);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const qLow = searchQuery.toLowerCase().trim();
        const apiResults = searchInApiData(qLow);
        let googleResults = [], coords = null;
        if (qLow.length >= 2) {
          googleResults = await searchGoogleMaps(searchQuery);
          if (googleResults[0]?.lat && googleResults[0]?.lng)
            coords = { lat: googleResults[0].lat, lng: googleResults[0].lng };
        }
        if (!coords && apiResults.length > 0) {
          const r = apiResults.find(r => r.lat && r.long);
          if (r) coords = { lat: r.lat, lng: r.long };
        }
        let nearby = [];
        if (coords) {
          nearby = findNearbyLocations(coords.lat, coords.lng, apiData)
            .filter(n => !apiResults.some(e => e.fullLocation === n.fullLocation))
            .map(l => ({
              ...l, isNearby: true,
              distance: calculateDistance(coords.lat, coords.lng, l.lat, l.long).toFixed(1),
            }))
            .sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));
        }
        const categorized = [
          ...apiResults.map(r   => ({ ...r, category: "exact",  section: "EXACT MATCHES"        })),
          ...nearby.map(r        => ({ ...r, category: "nearby", section: "NEARBY WITHIN 5KM"    })),
          ...googleResults.map(r => ({ ...r, category: "google", section: "LOCATION SUGGESTIONS" })),
        ];
        setSearchResults(categorized);
        setNearbyLocations(nearby);
        setShowSearchResults(true);
        setShowNoResults(categorized.length === 0);
      } catch {
        setSearchResults([]);
        setShowNoResults(true);
      } finally {
        setIsSearching(false);
      }
    }, 500);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchQuery, apiData, searchGoogleMaps]);

  const searchInApiData = qLow => {
    if (!apiData?.length) return [];
    const exact = [], partial = [];
    apiData.forEach(item => {
      const n = (item.name || "").toLowerCase(), f = (item.fullLocation || "").toLowerCase(), c = (item.city || "").toLowerCase();
      if (n === qLow || f === qLow) exact.push(item);
      else if (n.includes(qLow) || f.includes(qLow) || c.includes(qLow)) partial.push(item);
    });
    exact.sort((a, b) => (b.count || 0) - (a.count || 0));
    partial.sort((a, b) => {
      const am = (a.name || "").toLowerCase().includes(qLow), bm = (b.name || "").toLowerCase().includes(qLow);
      if (am && !bm) return -1; if (!am && bm) return 1;
      return (b.count || 0) - (a.count || 0);
    });
    return [...exact, ...partial];
  };

  const handleLocationSelect = location => {
    if (!location?.name) return;
    if (selectedLocations.length < 3 && !selectedLocations.some(l => l.name === location.name)) {
      setSelectedLocations([...selectedLocations, location]);
      setSearchQuery(""); setShowSearchResults(false); setShowNoResults(false);
    }
  };

  const handleLocationRemove = loc => {
    if (!loc?.name) return;
    setSelectedLocations(selectedLocations.filter(l => l?.name !== loc.name));
    if (loc.isCurrentLocation) setUseCurrentLocation(false);
  };

  const getValidLinkPatterns = () => {
    const base = {
      Purchase: [/^https?:\/\/landnest\.net\/buy-description\/\d+$/, /^https?:\/\/localhost:3000\/buy-description\/\d+$/, /^https?:\/\/app\.propertypincoder\.com\/buy-description\/\d+$/],
      Rent:     [/^https?:\/\/landnest\.net\/rent-description\/\d+$/, /^https?:\/\/localhost:3000\/rent-description\/\d+$/, /^https?:\/\/app\.propertypincoder\.com\/rent-description\/\d+$/],
      Lease:    [/^https?:\/\/landnest\.net\/lease-description\/\d+$/, /^https?:\/\/localhost:3000\/lease-description\/\d+$/, /^https?:\/\/app\.propertypincoder\.com\/lease-description\/\d+$/],
    };
    return base[selectedMainCategory] || [/^https?:\/\/landnest\.net\/.+\/\d+$/, /^https?:\/\/localhost:3000\/.+\/\d+$/, /^https?:\/\/app\.propertypincoder\.com\/.+\/\d+$/];
  };
  const isValidPropertyLink = link => getValidLinkPatterns().some(p => p.test(link));

  const handleSubmitResponse = async reqId => {
    const uid = localStorage.getItem("user_id") || "anonymous";
    if (!commentText.trim() && !propertyLink.trim()) { alert("Please enter either a comment or property link"); return; }
    if (propertyLink.trim() && !isValidPropertyLink(propertyLink.trim())) { alert(`Invalid property link for ${selectedMainCategory} section.`); return; }
    setSubmitting(true);
    try {
      const res = await fetch(RESPONSE_API_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comment: commentText, proerty_link: propertyLink, req_id: reqId, user_id: uid }),
      });
      if (res.ok) {
        const nr = await res.json();
        setResponses(prev => ({ ...prev, [reqId]: [...(prev[reqId] || []), nr] }));
        setCommentText(""); setPropertyLink(""); setActiveCommentIndex(null);
        alert("Response submitted successfully!");
        fetchResponses();
      } else throw new Error("Failed");
    } catch { alert("Failed to submit response. Please try again."); }
    finally { setSubmitting(false); }
  };

  const toggleResponses    = id => setExpandedResponses(p => ({ ...p, [id]: !p[id] }));
  const handleCommentClick = i  => { setActiveCommentIndex(activeCommentIndex === i ? null : i); setCommentText(""); setPropertyLink(""); };

  const getLocationIcon = (type, category) => {
    if (category === "nearby") return <NearMeIcon sx={{ color: colors.nearby }} />;
    if (category === "exact")  return <LocationOnIcon sx={{ color: colors.exact }} />;
    if (category === "google") return (
      <Box sx={{ width: 20, height: 20, borderRadius: "50%", backgroundColor: colors.google, display: "flex", alignItems: "center", justifyContent: "center", mr: 1 }}>
        <Typography variant="caption" sx={{ color: "white", fontWeight: "bold" }}>G</Typography>
      </Box>
    );
    switch (type) {
      case "city":     return <LocationOnIcon sx={{ color: colors.exact }}    />;
      case "locality": return <HomeWorkIcon   sx={{ color: "#388e3c" }}        />;
      case "project":  return <ApartmentIcon  sx={{ color: "#7b1fa2" }}        />;
      case "address":  return <LocationOnIcon sx={{ color: colors.google }}    />;
      case "current":  return <MyLocationIcon sx={{ color: "#4CAF50" }}        />;
      default:         return <LocationOnIcon sx={{ color: colors.secondary }} />;
    }
  };

  const safeGet = (o, p, d = "") => o && o[p] !== undefined ? o[p] : d;

  const getResultsBySection = () => {
    const s = {};
    searchResults.forEach(r => { const k = r.section || "OTHER"; if (!s[k]) s[k] = []; s[k].push(r); });
    return s;
  };
  const getSectionColor   = s => ({ "EXACT MATCHES": colors.exact, "NEARBY WITHIN 5KM": colors.nearby, "LOCATION SUGGESTIONS": colors.google }[s] || colors.primary);
  const getSectionBgColor = s => ({ "EXACT MATCHES": "#f0f7ff", "NEARBY WITHIN 5KM": "#e8f5e9", "LOCATION SUGGESTIONS": "#fff3e0" }[s] || "#f5f5f5");

  const renderResponseItem = (response, index) => (
    <Box key={index} sx={{ mb: 1, p: 1, backgroundColor: "white", borderRadius: 0.5, boxShadow: "0 1px 3px rgba(0,0,0,0.1)", border: "1px solid #e0e0e0" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
        <Avatar sx={{ width: 20, height: 20, mr: 0.75, bgcolor: colors.secondary, fontSize: "0.6rem", color: colors.textLight }}>
          {usersMap[response.user_id]?.charAt(0)?.toUpperCase() || "U"}
        </Avatar>
        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <Typography variant="caption" fontWeight="bold" color={colors.primary} sx={{ fontSize: "0.65rem" }}>
            {usersMap[response.user_id] || `User ${response.user_id}`}
          </Typography>
          <Typography variant="caption" color={colors.textMuted} sx={{ fontSize: "0.6rem" }}>
            {formatDate(response.created_at || new Date())}
          </Typography>
        </Box>
      </Box>
      {response.comment && (
        <Typography variant="body2" sx={{ color: colors.textDark, lineHeight: 1.2, fontSize: "0.7rem" }}>
          Click on below link to view the property.
        </Typography>
      )}
      {response.proerty_link && (
        <Box sx={{ mt: 0.5 }}>
          <Typography component="a" href={response.proerty_link} target="_blank" rel="noopener noreferrer"
            sx={{ color: "#1976d2", textDecoration: "underline", fontSize: "0.65rem", "&:hover": { color: "#1565c0" } }}>
            🔗 Property Link
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderPropertyCard = (property, index) => {
    const propLoc       = getPropertyLocation(property);
    const propResponses = responses[property.req_id] || [];
    return (
      <Card key={property.req_id} onClick={() => navigate(`/wanted-prop-details/${property.req_id}`)}
        sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.1)", overflow: "hidden", mb: 2, cursor: "pointer",
          "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)", transform: "translateY(-2px)", transition: "all 0.2s ease-in-out" } }}>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <Box sx={{ p: 1, display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: colors.primary, borderBottom: `2px solid ${colors.secondary}` }}>
            <Box sx={{ backgroundColor: "#b9a990", color: colors.primary, px: 1.5, py: 0.5, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "0.9rem", lineHeight: 1 }}>{formatCategoryName(property.looking_for)}</Typography>
            </Box>
            <Box sx={{ backgroundColor: colors.cardBackground, color: colors.primary, px: 1.5, py: 0.5, borderRadius: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1, fontSize: "0.9rem" }}>Budget: {formatBudget(property.min_budget, property.max_budget)}</Typography>
            </Box>
          </Box>

          <Box sx={{ p: 1.5, borderLeft: `2px solid ${colors.secondary}`, borderRight: `2px solid ${colors.secondary}`, borderBottom: `2px solid ${colors.secondary}`, borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px" }}>
            <Box sx={{ display: "grid", gap: 0.75 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <Typography variant="body2" color={colors.textDark} sx={{ fontSize: "0.75rem", lineHeight: 1.2, minWidth: 90, flexShrink: 0 }}><strong>Property Type:</strong></Typography>
                <Typography variant="body2" color={colors.textDark} sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>{property.property_type || "Not specified"}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <Typography variant="body2" color={colors.textDark} sx={{ fontSize: "0.75rem", lineHeight: 1.2, minWidth: 90, flexShrink: 0 }}><strong>Location:</strong></Typography>
                <Typography variant="body2" color={colors.textDark} sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>{propLoc}</Typography>
              </Box>
              {formatDimensions(property) && (
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Typography variant="body2" color={colors.textDark} sx={{ fontSize: "0.75rem", lineHeight: 1.2, minWidth: 90, flexShrink: 0 }}><strong>Length*Width:</strong></Typography>
                  <Typography variant="body2" color={colors.textDark} sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>{formatDimensions(property)}</Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", alignItems: "flex-start", mt: 0.5 }}>
                <Avatar sx={{ width: 16, height: 16, fontSize: "0.5rem" }}>👤</Avatar>
                <Typography variant="body2" color={colors.textDark} sx={{ fontSize: "0.75rem", lineHeight: 1.2, minWidth: 75, flexShrink: 0 }}>
                  <strong>{usersMap[property.user_id] || `CUST ${property.user_id}`}</strong>
                </Typography>
                <Typography variant="body2" color={colors.textDark} sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>{formatDate(property.created_at)}</Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 1, pt: 1, borderTop: "1px solid #e0e0e0" }}>
              <Button size="small"
                onClick={e => { e.stopPropagation(); requireLogin(e, () => toggleResponses(property.req_id)); }}
                sx={{ color: colors.secondary, fontSize: "0.65rem", minWidth: "auto", px: 0.5, textTransform: "none", fontWeight: "bold" }}
                endIcon={expandedResponses[property.req_id] ? <ExpandLessIcon sx={{ fontSize: "0.8rem" }} /> : <ExpandMoreIcon sx={{ fontSize: "0.8rem" }} />}>
                {expandedResponses[property.req_id] ? "Hide" : "Show"} Responses
              </Button>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <ChatBubbleOutlineIcon sx={{ fontSize: "0.8rem", color: colors.secondary }} />
                <Typography variant="body2" sx={{ fontSize: "0.7rem", color: colors.secondary, fontWeight: "bold" }}>{propResponses.length}</Typography>
                <IconButton size="small"
                  onClick={e => { e.stopPropagation(); requireLogin(e, () => handleCommentClick(index)); }}
                  sx={{ color: colors.textLight, backgroundColor: colors.secondary, "&:hover": { backgroundColor: colors.accent }, width: 24, height: 24 }}>
                  <ChatBubbleOutlineIcon sx={{ fontSize: "0.8rem" }} />
                </IconButton>
              </Box>
            </Box>

            {propResponses.length > 0 && (
              <Collapse in={expandedResponses[property.req_id]}>
                <Box sx={{ p: 1, backgroundColor: "#fafafa", borderLeft: `2px solid ${colors.secondary}`, borderRight: `2px solid ${colors.secondary}`, borderBottom: `2px solid ${colors.secondary}`, borderTop: "none", borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px", mt: 1 }}>
                  {propResponses.slice(0, 1).map((r, ri) => (<Box key={ri} className="no-navigate">{renderResponseItem(r, ri)}</Box>))}
                  {propResponses.length > 1 && (
                    <Box sx={{ textAlign: "center", mt: 1 }}>
                      <Button size="small"
                        onClick={e => { e.stopPropagation(); navigate("/all-responses", { state: { responses: propResponses, propertyDetails: property } }); }}
                        sx={{ color: colors.secondary, fontSize: "0.65rem", textTransform: "none", fontWeight: "bold" }}>
                        + {propResponses.length - 1} more responses
                      </Button>
                    </Box>
                  )}
                </Box>
              </Collapse>
            )}

            {activeCommentIndex === index && (
              <Box sx={{ p: 1, backgroundColor: "#f5f2ee", borderLeft: `2px solid ${colors.secondary}`, borderRight: `2px solid ${colors.secondary}`, borderBottom: `2px solid ${colors.secondary}`, borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px", mt: 1 }} className="no-navigate">
               <MuiTextField
  fullWidth
  variant="outlined"
  placeholder="Paste property link"
  value={propertyLink}
  onChange={e => setPropertyLink(e.target.value)}
  onClick={(e) => e.stopPropagation()}
  onFocus={(e) => e.stopPropagation()}
  size="small"
  sx={{ mb: 1 }}
                  InputProps={{ sx: { fontSize: "0.75rem", "& input": { py: 0.75 } } }} />
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                  <Button size="small" onClick={e => { e.stopPropagation(); handleCommentClick(index); }} sx={{ color: colors.secondary, fontSize: "0.65rem", minWidth: "auto", px: 1 }}>Cancel</Button>
                  <Button variant="contained" size="small"
                    onClick={e => { e.stopPropagation(); handleSubmitResponse(property.req_id); }}
                    disabled={submitting || (!commentText.trim() && !propertyLink.trim())}
                    sx={{ backgroundColor: colors.secondary, color: colors.textLight, fontSize: "0.65rem", minWidth: "auto", px: 1, "&:hover": { backgroundColor: colors.accent } }}>
                    {submitting ? "..." : "Submit"}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // ─── Properties list section — extracted for clarity ──────────────────────
  const renderPropertiesSection = () => {
    // Always show skeleton while loading (initial load OR filter change)
    if (initialLoading) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
          {[1, 2, 3].map(i => <SkeletonCard key={i} />)}
        </Box>
      );
    }

    if (recentProperties.length === 0) {
      return (
        <Box sx={{ textAlign: "center", mt: 4, p: 3, backgroundColor: "#fff", borderRadius: 2, border: "1px dashed #ccc" }}>
          <Typography variant="body1" sx={{ color: colors.textMuted, mb: 1 }}>No properties found</Typography>
          <Typography variant="caption" sx={{ color: colors.textMuted, display: "block", mb: 2 }}>
            {selectedMainCategory ? `No ${selectedMainCategory} requests` : "No property requests"}
            {selectedPropertyTypes.length > 0 && ` for "${selectedPropertyTypes.join(", ")}"`}
            {selectedLocations.length > 0 && " in selected location"}
          </Typography>
          {(selectedPropertyTypes.length > 0 || selectedLocations.length > 0) && (
            <Button variant="contained" size="small"
              onClick={() => { setSelectedPropertyTypes([]); setSelectedLocations([]); }}
              sx={{ backgroundColor: colors.secondary, color: "#fff", textTransform: "none", fontSize: "0.8rem", "&:hover": { backgroundColor: colors.accent } }}>
              Clear Filters
            </Button>
          )}
        </Box>
      );
    }

    return (
      <>
        {/* <Typography variant="caption" sx={{ color: colors.textMuted, mb: 1, display: "block" }}>
          Showing {recentProperties.length} properties{hasMore ? " — scroll for more" : " (all loaded)"}
        </Typography> */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {recentProperties.map((property, index) => renderPropertyCard(property, index))}
        </Box>
        <ScrollSentinel onVisible={loadMore} hasMore={hasMore} loading={listLoading} />
      </>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", backgroundColor: colors.background, position: "relative" }}>

      <AppBar sx={{ backgroundColor: colors.primary, color: "#fff", position: "fixed", top: 0, left: 0, right: 0, zIndex: 1200 }}>
        <Toolbar>
          <IconButton onClick={() => navigate(-1)} sx={{ color: "#fff" }}><ArrowBackIosNewIcon /></IconButton>
          <Typography variant="h6" sx={{ ml: 2 }}>Property Search</Typography>
        </Toolbar>
      </AppBar>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: "100%" }}>{error}</Alert>
      </Snackbar>

      <Box sx={{ p: 1, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Search bar */}
        <Box sx={{ position: "relative", mb: 0.8, mt: 8 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <Box sx={{ flex: "0 0 85%", position: "relative" }}>
              <TextField fullWidth variant="outlined" placeholder="Search for localities.."
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onFocus={() => { if (searchQuery.trim()) setShowSearchResults(true); }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: colors.secondary }} /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      {isSearching ? <CircularProgress size={20} /> : searchQuery ? (
                        <IconButton size="small" onClick={() => setSearchQuery("")} sx={{ p: 0 }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
                      ) : null}
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, height: "46px", backgroundColor: "#fff", "&:hover fieldset": { borderColor: colors.secondary }, "&.Mui-focused fieldset": { borderColor: colors.secondary } } }}
              />

              {showSearchResults && searchQuery.trim() && (
                <Paper elevation={1} sx={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1100, mt: 1, maxHeight: 450, overflow: "auto", border: `1px solid ${colors.secondary}`, boxShadow: 3 }}>
                  {showNoResults && searchResults.length === 0 && (
                    <Box sx={{ p: 1.2, textAlign: "center" }}>
                      <WarningIcon sx={{ fontSize: 32, color: colors.warning }} />
                      <Typography variant="body1" sx={{ fontWeight: "medium", color: colors.primary, mb: 0.4, fontSize: "0.95rem" }}>No results found</Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.85rem" }}>Try a different search term</Typography>
                    </Box>
                  )}
                  {Object.entries(getResultsBySection()).map(([section, sectionResults], si) => (
                    <React.Fragment key={section}>
                      <Box sx={{ p: 0.7, backgroundColor: getSectionBgColor(section), borderBottom: `1px solid ${getSectionColor(section)}33` }}>
                        <Typography variant="caption" sx={{ fontWeight: "bold", color: getSectionColor(section), fontSize: "0.8rem" }}>
                          {section} ({sectionResults.length})
                        </Typography>
                        {section === "NEARBY WITHIN 5KM" && (
                          <Typography variant="caption" sx={{ color: getSectionColor(section), ml: 1, fontStyle: "italic", fontSize: "0.75rem" }}>(from your search location)</Typography>
                        )}
                      </Box>
                      <List dense sx={{ py: 0 }}>
                        {sectionResults.map((result, ri) => (
                          <React.Fragment key={`${section}-${ri}`}>
                            <ListItem button onClick={() => handleLocationSelect(result)} sx={{ py: 0.15, minHeight: "40px", "&:hover": { backgroundColor: `${getSectionColor(result.section)}11` } }}>
                              <ListItemIcon sx={{ minWidth: "36px" }}>{getLocationIcon(result.type, result.category)}</ListItemIcon>
                              <ListItemText sx={{ my: 0.3 }}
                                primary={
                                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.2 }}>
                                    <Typography variant="body2" sx={{ fontWeight: "medium", fontSize: "0.92rem" }}>{safeGet(result, "name")}</Typography>
                                    {result.distance && <Chip label={`${result.distance} km`} size="small" sx={{ backgroundColor: colors.nearby, color: "#fff", fontSize: "0.65rem", height: "18px", "& .MuiChip-label": { px: 0.4 } }} />}
                                  </Box>
                                }
                                secondary={
                                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", fontSize: "0.78rem", lineHeight: 1.5 }}>
                                    {safeGet(result, "fullLocation") || safeGet(result, "description") || safeGet(result, "fullAddress")}
                                  </Typography>
                                }
                              />
                              {result.count > 0 ? (
                                <Chip label={result.count} size="small" sx={{ backgroundColor: getSectionColor(section), color: "#fff", fontWeight: "bold", fontSize: "0.75rem", minWidth: "30px", height: "22px" }} />
                              ) : result.category === "google" && (
                                <Typography variant="caption" sx={{ color: colors.google, fontStyle: "italic", fontSize: "0.7rem" }}>Search here</Typography>
                              )}
                            </ListItem>
                            {ri < sectionResults.length - 1 && <Divider sx={{ my: 0.3 }} />}
                          </React.Fragment>
                        ))}
                      </List>
                      {si < Object.keys(getResultsBySection()).length - 1 && <Divider sx={{ my: 0.4 }} />}
                    </React.Fragment>
                  ))}
                </Paper>
              )}
            </Box>

            <Box sx={{ flex: "0 0 15%", display: "flex", justifyContent: "center" }}>
              <IconButton
                onClick={() => {
                  const ex = selectedLocations.find(l => l.isCurrentLocation);
                  if (ex) { setSelectedLocations(selectedLocations.filter(l => !l.isCurrentLocation)); setUseCurrentLocation(false); }
                  else { setUseCurrentLocation(true); getCurrentLocation(); }
                }}
                sx={{
                  width: "50px", height: "46px",
                  border: `2px solid ${selectedLocations.some(l => l.isCurrentLocation) ? "#4CAF50" : colors.secondary}`,
                  borderRadius: 2,
                  color: selectedLocations.some(l => l.isCurrentLocation) ? "#4CAF50" : colors.secondary,
                  backgroundColor: selectedLocations.some(l => l.isCurrentLocation) ? "#e8f5e9" : "#fff",
                  "&:hover": { backgroundColor: `${colors.secondary}11` },
                }}>
                {isSearching ? <CircularProgress size={20} sx={{ color: colors.secondary }} /> : <LocationOnIcon />}
              </IconButton>
            </Box>
          </Box>

          {selectedLocations.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "row", overflowX: "auto", flexWrap: "nowrap", gap: 1, mt: 1, pb: 0.5, "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none" }}>
              {selectedLocations.map((location, i) => (
                <Chip key={i} icon={getLocationIcon(safeGet(location, "type"))}
                  label={getLocationSearchKeyword(location)}
                  onDelete={() => handleLocationRemove(location)}
                  sx={{ flexShrink: 0, backgroundColor: location.isNearby ? colors.nearby : colors.secondary, color: "#fff", fontWeight: "bold", "& .MuiChip-icon": { color: "#fff !important" }, "& .MuiChip-deleteIcon": { color: "#fff" } }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Category selector */}
        <Box sx={{ mb: 1, backgroundColor: "#fff", borderRadius: 2, overflow: "hidden", border: "1px solid #e0e0e0" }}>
          <Typography variant="h7" sx={{ p: 0.8, fontWeight: "bold", display: "flex", alignItems: "center", color: colors.primary, borderBottom: "1px solid #e0e0e0" }}>Category</Typography>
          <Box sx={{ p: 0.6 }}>
            <Box sx={{ display: "flex", flexDirection: "row", overflowX: "auto", gap: 1, pb: 1, "&::-webkit-scrollbar": { height: 4 }, "&::-webkit-scrollbar-thumb": { background: "#c1c1c1", borderRadius: 2 }, scrollbarWidth: "thin" }}>
              {MAIN_CATEGORIES.map(cat => (
                <Button key={cat.id}
                  variant={selectedMainCategory === cat.id ? "contained" : "outlined"}
                  onClick={() => {
                    if (selectedMainCategory === cat.id) { setSelectedMainCategory(""); setSelectedPropertyTypes([]); }
                    else setSelectedMainCategory(cat.id);
                  }}
                  sx={{ borderRadius: 2, borderColor: selectedMainCategory === cat.id ? colors.primary : "#e0e0e0", backgroundColor: selectedMainCategory === cat.id ? colors.primary : "transparent", color: selectedMainCategory === cat.id ? "#fff" : colors.secondary, textTransform: "none", fontWeight: selectedMainCategory === cat.id ? "bold" : "normal", whiteSpace: "nowrap", flexShrink: 0, minWidth: "auto", px: 2, "&:hover": { borderColor: colors.primary, backgroundColor: selectedMainCategory === cat.id ? colors.primary : "rgba(0,0,0,0.04)" } }}>
                  {cat.label}
                </Button>
              ))}
            </Box>

            {selectedMainCategory && (
              <Box sx={{ mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontSize: "14px", mb: 1, fontWeight: "bold", color: colors.primary, display: "inline-flex", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
                  Property Type for "{selectedMainCategory}"
                </Typography>
                <Chip label={selectedPropertyTypes.length > 0 ? `${selectedPropertyTypes.length} selected` : "Select Property Types"}
                  onClick={() => setPropertyTypeDialogOpen(true)} variant="outlined" deleteIcon={<AddIcon />} onDelete={() => setPropertyTypeDialogOpen(true)}
                  sx={{ borderColor: colors.secondary, color: colors.secondary, "& .MuiChip-deleteIcon": { color: colors.secondary } }} />
                {selectedPropertyTypes.length > 0 && (
                  <Box sx={{ display: "flex", flexDirection: "row", overflowX: "auto", flexWrap: "nowrap", gap: 1, mt: 2, pb: 0.5, "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none" }}>
                    {selectedPropertyTypes.map((type, i) => (
                      <Chip key={i} label={type} onDelete={() => setSelectedPropertyTypes(selectedPropertyTypes.filter(t => t !== type))}
                        sx={{ flexShrink: 0, backgroundColor: colors.secondary, color: "#fff", "& .MuiChip-deleteIcon": { color: "#fff" } }} />
                    ))}
                  </Box>
                )}
                {selectedPropertyTypes.length === 0 && (
                  <Typography variant="caption" sx={{ color: colors.warning, display: "block", mt: 1 }}>Please select at least one property type</Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Properties list */}
        <Box sx={{ mb: 1, flex: 1, overflowY: "auto", pb: 10 }}>
          <Typography variant="h7" sx={{ mb: 1, fontWeight: "bold", color: colors.primary, display: "block" }}>
            {selectedMainCategory ? `Recent ${selectedMainCategory} Requests` : "Recent Property Requests"}
            {selectedLocations.length > 0 && " in Selected Location"}
          </Typography>
          {renderPropertiesSection()}
        </Box>
      </Box>

      {/* Property type dialog */}
      <Dialog open={propertyTypeDialogOpen} onClose={() => setPropertyTypeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Select Property Type for "{selectedMainCategory || "Property"}"</Typography>
            <IconButton onClick={() => setPropertyTypeDialogOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>Select one or more property types</Typography>
          {availablePropertyTypes.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body2" color="text.secondary">No property types available for "{selectedMainCategory || "this category"}"</Typography>
            </Box>
          ) : (
            <List sx={{ pt: 0 }}>
              {availablePropertyTypes.map((type, i) => (
                <ListItem key={i}
                  onClick={() => {
                    if (selectedPropertyTypes.includes(type)) setSelectedPropertyTypes(selectedPropertyTypes.filter(t => t !== type));
                    else setSelectedPropertyTypes([...selectedPropertyTypes, type]);
                  }}
                  sx={{ borderBottom: "1px solid #e0e0e0", "&:last-child": { borderBottom: "none" }, "&:hover": { backgroundColor: "rgba(139,115,85,0.1)" }, cursor: "pointer" }}>
                  <Checkbox checked={selectedPropertyTypes.includes(type)} sx={{ color: colors.secondary }} />
                  <ListItemText primary={type} primaryTypographyProps={{ fontWeight: "medium" }} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => {
            setPropertyTypeDialogOpen(false);
            if (selectedPropertyTypes.length === 0 && selectedMainCategory) setError("Please select at least one property type");
          }} variant="contained" fullWidth sx={{ backgroundColor: colors.secondary, color: "#fff", "&:hover": { backgroundColor: colors.accent } }}>
            Done ({selectedPropertyTypes.length} selected)
          </Button>
        </DialogActions>
      </Dialog>

      <GuestLoginModal open={showGuestModal} onClose={() => setShowGuestModal(false)} />
      <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0 }}>
        <FormsBottomNavbar />
      </Box>
    </Box>
  );
};

export default PropertySearchFilters;